import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { Dimensions, Modal, useColorScheme } from "react-native";
import { useAccessViewport } from "../../config";
import { theme } from "@theme";
import { getViewPort } from "@utils/web/config";
import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LIcon from "@components/LIcon";
import LText from "@components/LText";
import React, { useEffect, useState } from "react";
import Button from "./Button";

const enterEasing = Easing.out(Easing.cubic);
const exitEasing = Easing.in(Easing.cubic);

// Calculate the offset between full screen and viewport (for web when viewport is centered)
const getViewportOffset = () => {
  const fullWindow = Dimensions.get("window");
  const viewport = getViewPort(fullWindow);

  return {
    x: fullWindow.width - viewport.width,
    y: fullWindow.height - viewport.height,
  };
};

export const ContextMenu = ({
  visible,
  onClose,
  options,
  buttonLayout,
  placement = "above",
}: {
  visible: boolean;
  onClose: () => void;
  options: Array<{
    content: React.ReactNode;
    onPress: () => void;
    key: string;
  }>;
  buttonLayout: { x: number; y: number; width: number; height: number } | null;
  placement?: "above" | "below";
}) => {
  const { width: screenWidth, height: screenHeight } = useAccessViewport();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const scale = useSharedValue(0.94);
  const opacity = useSharedValue(0);
  const backdropOpacity = useSharedValue(0);

  useEffect(() => {
    if (visible) {
      scale.set(withTiming(1, { duration: 140, easing: enterEasing }));
      opacity.set(withTiming(1, { duration: 100, easing: enterEasing }));
      backdropOpacity.set(
        withTiming(1, { duration: 120, easing: enterEasing }),
      );
    } else {
      scale.set(0.94);
      opacity.set(0);
      backdropOpacity.set(0);
    }
  }, [visible]);

  const handleClose = () => {
    scale.set(withTiming(0.96, { duration: 90, easing: exitEasing }));
    opacity.set(withTiming(0, { duration: 80, easing: exitEasing }));
    backdropOpacity.set(
      withTiming(0, { duration: 100, easing: exitEasing }, () => {
        runOnJS(onClose)();
      }),
    );
  };

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  if (!buttonLayout) return null;

  // Adjust button coordinates by subtracting viewport offset
  const viewportOffset = getViewportOffset();
  const adjustedX = buttonLayout.x - viewportOffset.x;
  const adjustedY = buttonLayout.y - viewportOffset.y;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={handleClose}
    >
      <Animated.View style={[{ flex: 1 }, backdropStyle]}>
        <LPressable
          sentryEventName="ContextMenu.Backdrop"
          onPress={handleClose}
          style={{
            flex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.35)",
          }}
        >
          <Animated.View
            style={[
              {
                position: "absolute",
                right: screenWidth - (adjustedX + buttonLayout.width),
                ...(placement === "above"
                  ? {
                      bottom: screenHeight - adjustedY + theme.spacing.s3,
                      transformOrigin: "75% 100%",
                    }
                  : {
                      top: adjustedY + buttonLayout.height + theme.spacing.s3,
                      transformOrigin: "75% 0%",
                    }),
              },
              animatedStyle,
            ]}
          >
            <LPressable
              sentryEventName="ContextMenu.Content"
              onPress={(e) => e.stopPropagation()}
            >
              <LBox
                borderRadius="m-l"
                overflow="hidden"
                minWidth={200}
                style={{
                  backgroundColor: isDark ? "#1C1C1E" : "#FFFFFF",
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.3,
                  shadowRadius: 8,
                  elevation: 8,
                }}
              >
                {options.map((option, index) => (
                  <LPressable
                    key={option.key}
                    sentryEventName={`ContextMenu.${option.key}`}
                    onPress={() => {
                      option.onPress();
                      handleClose();
                    }}
                  >
                    <LBox
                      flexDirection="row"
                      alignItems="center"
                      px="s4"
                      py="s3_5"
                      borderBottomWidth={index < options.length - 1 ? 1 : 0}
                      style={{
                        borderBottomColor: isDark
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      {option.content}
                    </LBox>
                  </LPressable>
                ))}
              </LBox>
            </LPressable>
          </Animated.View>
        </LPressable>
      </Animated.View>
    </Modal>
  );
};

const More = ({
  options,
  width,
}: {
  width: number;
  options: Array<{
    key: string;
    content: React.ReactNode;
    onPress: () => void;
  }>;
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [buttonLayout, setButtonLayout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const buttonRef = React.useRef<any>(null);

  const handleOpenMenu = () => {
    buttonRef.current?.measure(
      (
        x: number,
        y: number,
        width: number,
        height: number,
        pageX: number,
        pageY: number,
      ) => {
        setButtonLayout({ x: pageX, y: pageY, width, height });
        setShowMenu(true);
      },
    );
  };

  return (
    <>
      <LBox ref={buttonRef} collapsable={false}>
        <Button.Button
          onPress={handleOpenMenu}
          sentryEventName="AccessDetail.More"
          bg="whiteTranslucent20"
          py="s2"
          width={width}
        >
          <LIcon
            colors={{ color: "#FFFFFF80" }}
            name="ellipsis"
            size={16}
            strokeWidth={2.25}
            rotate={90}
          />
          <LText variant="bodyM" color="white">
            More
          </LText>
        </Button.Button>
      </LBox>
      <ContextMenu
        visible={showMenu}
        onClose={() => setShowMenu(false)}
        options={options}
        buttonLayout={buttonLayout}
      />
    </>
  );
};

export default More;
